import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    forwardRef,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    viewChild
} from '@angular/core';
import { ProductListingComponentDef } from '@df/catalog-ui/product/listing/product-listing.component.def';
import { Category, ICategoryDataV3 } from '@df/catalog/category/category';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { AbService } from '@df/module-ab/ab.service';
import { Gtm } from '@df/module-gtm/gtm.namespace';
import { GtmTrackViewItemListDirective } from '@df/module-gtm/track/view-item-list/gtm-track-view-item-list.directive';
import { InfiniteScrollDirective } from '@df/module-infinite-scroll/infinite-scroll.directive';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { OptionLabelPipe } from '@df/ui/common/option-label.pipe';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { Subscription } from 'rxjs';
import { SkeletonProductComponent } from '../../../common/skeleton-loader/product/skeleton-product.component';

import { SkeletonWrapperComponent } from '../../../common/skeleton-loader/wrapper/skeleton-wrapper.component';
import { BackToTopComponent } from '../../../layout/page/back-to-top.component';
import { CdkMultiColumnsVirtualScrollDirective } from '../../../ui/cdk/scrolling/multi-columns-virtual-scroll';
import { ProductBannerComponent } from '../product/banner/product-banner.component';
import { ProductComponent } from '../product/product.component';
import { AspectRatioPipe } from '../product/ui/aspect-ratio.pipe';
import { ProductColSpanPipe } from '../product/ui/product-col-span.pipe';
import { ProductRowSpanPipe } from '../product/ui/product-row-span.pipe';
import { ProductWidthPipe } from '../product/ui/product-width.pipe';

@Component({
    selector: 'product-listing',
    templateUrl: './product-listing.component.html',
    styleUrl: './product-listing.component.scss',
    standalone: true,
    imports: [
        AspectRatioPipe,
        BackToTopComponent,
        CdkMultiColumnsVirtualScrollDirective,
        CommonModule,
        GridModule,
        GtmTrackViewItemListDirective,
        InfiniteScrollDirective,
        OptionLabelPipe,
        forwardRef(() => ProductBannerComponent),
        ProductColSpanPipe,
        ProductComponent,
        ProductRowSpanPipe,
        ProductWidthPipe,
        ScrollingModule,
        SkeletonWrapperComponent,
        SkeletonProductComponent,
        TranslatePipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductListingComponent extends ProductListingComponentDef implements OnChanges, OnInit, OnDestroy {
    @Input()
    override category?: Category<ICategoryDataV3>;

    readonly virtualScroll = viewChild<CdkVirtualScrollViewport>('virtualScrollRef');

    plpProductNameVariant?: string;
    quickA2BVariant?: string;

    modelView = false;
    listingSize = 2;
    modelsFiltered: Product<IProductDataV2>[] = [];

    private abService = inject(AbService);
    private listingSizeSubscription?: Subscription;
    private _modelsChanged = false;

    get listId(): string {
        return this.category?.data?.name?.toLowerCase() || 'category';
    }

    get listName(): string {
        return this.category?.data?.name || 'Category';
    }

    get gtmTrackViewItemList(): Gtm.ITrackViewItemList {
        return {
            listId: this.listId,
            listName: this.listName,
            models: this.listing?.models || []
        };
    }

    get displayModelViewSwitch(): boolean {
        return !!this.category?.data?.alt_view_allowed;
    }

    get productHeight(): number {
        return (
            (this.listingSize === 2 ? (this.client.isS && this.plpProductNameVariant === 'B' ? 356 : 400) : 615) +
            (this.quickA2BVariant === 'B' ? 32 : 0)
        );
    }

    get skeletonArray(): number[] {
        if (this.category?.products && this.category.products.length > 0) {
            const skeletons = Math.min(this.listingSize * 2, this.category.products.length);

            return Array.from({ length: skeletons }, (_, i) => i);
        }

        return [];
    }

    trackByFn = (index: number, item: Product<IProductDataV2>) => item.id;

    override ngOnInit(): void {
        this.changeDetectorRef.reattach();

        const defaultSize = this.moduleConfig.listing.category.defaultSize || [2, 2, 5, 5];

        this.listing?.size?.setDefaultSize([
            this.category?.data?.columns_s || defaultSize[0],
            this.category?.data?.columns_m || defaultSize[1],
            this.category?.data?.columns_l || defaultSize[2],
            this.category?.data?.columns_l || defaultSize[3]
        ]);

        this.listingSize = this.listing?.size?.getState() || defaultSize[this.client.sizeId];

        this.subscriptions.add(
            this.abService.getSubject('plpProductName').subscribe(value => {
                const variantId = value.variant;
                if (this.plpProductNameVariant !== variantId) {
                    this.plpProductNameVariant = variantId;
                    this.detectChanges();
                }
            })
        );
        this.plpProductNameVariant = this.abService.getVariant('plpProductName');
        this.subscriptions.add(
            this.abService.getSubject('quickA2B').subscribe(value => {
                const variantId = value.variant;
                if (this.quickA2BVariant !== variantId) {
                    this.quickA2BVariant = variantId;
                    this.detectChanges();
                }
            })
        );
        this.quickA2BVariant = this.abService.getVariant('quickA2B');

        super.ngOnInit();
    }

    override ngOnChanges(changes: SimpleChanges): void {
        if (changes['category']) {
            this.selectModelView(!!this.category?.data?.alt_view_default);
        }
        super.ngOnChanges(changes);
    }

    override ngOnDestroy(): void {
        this.listingSizeSubscription?.unsubscribe();

        super.ngOnDestroy();
    }

    selectModelView(to: boolean): void {
        const was = this.modelView;

        if (this.displayModelViewSwitch) {
            this.modelView = to;
        } else {
            this.modelView = false;
        }

        if (was !== this.modelView) {
            this.detectChanges();
        }
    }

    onFiltersOpen() {
        this._modelsChanged = false;
    }

    onFiltersClose() {
        if (this._modelsChanged) {
            this._modelsChanged = false;
            this.returnToListingTop();
        }
    }

    onRenderedOffsetChanged(_index: number) {
        this.detectChanges();
    }

    protected override onListingUpdate(): void {
        this.listingSize = this.listing?.size?.getState() || 2;

        this.modelsFiltered = this.listing?.models || [];
        this.virtualScroll()?.scrollToIndex(0);

        super.onListingUpdate();
    }

    protected override onModelsUpdate() {
        super.onModelsUpdate();
        this._modelsChanged = true;
    }
}
